/**
 * The time-of-day windows (CHG-2026-09-manager-reporting).
 *
 * Deliberately **not** derived from the store's staffed-hours schedule: `scheduling_json` holds the
 * current configuration only and is not historised, so applying today's opening hours to last
 * month's revenue would produce a confident number about a schedule that may never have existed. A
 * manual window is the more honest one.
 *
 * The boundaries are mirrored in `SalesItemReportRepository.daypartKey`, which cuts the `daypart`
 * dimension server-side. Changing them means changing both.
 */
export type DaypartKey =
  | 'early'
  | 'morning'
  | 'noon'
  | 'afternoon'
  | 'evening'
  | 'night'

export interface Daypart {
  key: DaypartKey
  /** Inclusive local hour. */
  hourFrom: number
  /** Exclusive local hour. */
  hourTo: number
}

/** The five presets the UI offers, in clock order. */
export const DAYPART_PRESETS: Array<Daypart> = [
  { key: 'early', hourFrom: 6, hourTo: 9 },
  { key: 'morning', hourFrom: 9, hourTo: 11 },
  { key: 'noon', hourFrom: 11, hourTo: 14 },
  { key: 'afternoon', hourFrom: 14, hourTo: 16 },
  { key: 'evening', hourFrom: 16, hourTo: 20 },
]

/**
 * The sixth bucket the server also produces. It is not offered as a preset — nobody asks "how was
 * business at three in the morning" — but a `daypart` breakdown can return it, and a bar the UI
 * cannot label would be worse than one it can.
 */
export const NIGHT: Daypart = { key: 'night', hourFrom: 20, hourTo: 6 }

export function daypartByKey(key: string): Daypart | undefined {
  if (key === 'night') return NIGHT
  return DAYPART_PRESETS.find((d) => d.key === key)
}

/** `06:00–09:00`, for the axis and the filter chip. */
export function daypartRangeLabel(part: Daypart): string {
  const pad = (h: number) => String(h).padStart(2, '0')
  return `${pad(part.hourFrom)}:00–${pad(part.hourTo)}:00`
}
